import { FollowUpTracePayload, StrategyTracePayload } from '../models/conversation-trace.interface';

/**
 * The copy map: BE NextStep keys / follow-up types → owner-facing language. Everything here keys on
 * stable BE identifiers; anything not found falls back to a humanized key, never crashes.
 *
 * The trace table is append-only, so a key the BE has retired keeps arriving on historical rows
 * forever. Retired keys stay in these maps — marked as such — and are never swapped for their
 * replacement; adding the new key beside them is the whole migration.
 */

/** The opener step — excluded from the segment ladder; it gets its own "First reply" chip instead. */
export const openerStep = 'WELCOME_USER';

/** The IDV step — folded into the identity-verification chip when the turn also carries its trace. */
export const identityVerificationStep = 'HANDLE_IDENTITY_VERIFICATION';

/** Steps that never join the normal segment ladder. */
export const hiddenSteps = [openerStep];

/** The three screening facts collected before the tour gate opens. */
export const screeningSteps = ['DISCUSS_MOVE_IN_DATE', 'DISCUSS_CREDIT_SCORE', 'DISCUSS_INCOME'];

export const bookingSteps = ['SCHEDULE_SHOWING', 'RESCHEDULE_SHOWING'];

/**
 * The showing attendance-and-access step. It replaced RENTER_CONFIRMS_SHOWING,
 * FOLLOW_UP_ON_ACCESS_CODE, HANDLE_SHOWING_ATTENDANCE_ISSUES and ACCESS_INSTRUCTIONS_NOT_WORKING
 * (2026-10), and its chip depends on what it did: see showingLogisticsCopy.
 */
export const showingLogisticsStep = 'HANDLE_SHOWING_LOGISTICS';

export interface StepMeta {
  /** Full label used in the panel's per-part breakdown. */
  label: string;
  /** One or two sentences for the panel body — what Tallo did and why it matters to the owner. */
  body: string;
}

export const stepMeta: Record<string, StepMeta> = {
  DISCUSS_MOVE_IN_DATE: {
    label: 'Move-in date',
    body: "Reads the renter's timing and stores it on their screening profile — one of the three facts collected before the tour gate opens.",
  },
  DISCUSS_CREDIT_SCORE: {
    label: 'Credit score',
    body: 'Picks the credit figure out of the message and folds it into the screening profile. Nothing is pulled — this is the conversational ballpark.',
  },
  DISCUSS_INCOME: {
    label: 'Income',
    body: 'Picks the income figure out of the message and folds it into the screening profile.',
  },
  SCHEDULE_SHOWING: {
    label: 'Scheduling',
    body: "Handles the tour ask — checks this property's showing windows and moves the booking forward.",
  },
  RESCHEDULE_SHOWING: {
    label: 'Rescheduling',
    body: 'Continues the time negotiation for an existing booking instead of starting a new one.',
  },
  ANSWER_PROPERTY_QUESTION: {
    label: 'Property question',
    body: "Answers from the listing facts you've given Tallo for this property.",
  },
  /** Retired into HANDLE_APPLICATION — kept for the historical rows still carrying this key. */
  ANSWER_APPLICATION_PROCESS_QUESTION: {
    label: 'Application question',
    body: 'Explains how applying works here — see the home first, then the invite comes from you after the showing.',
  },
  /** Retired into HANDLE_SHOWING_LOGISTICS — kept for the historical rows still carrying this key. */
  RENTER_CONFIRMS_SHOWING: {
    label: 'Attendance confirmed',
    body: 'The renter confirmed they will attend, so the reminder loop stops and the showing stays on the calendar.',
  },
  /** Rows written before the step recorded its leaf; newer rows go through showingLogisticsCopy. */
  HANDLE_SHOWING_LOGISTICS: {
    label: 'Showing logistics',
    body: "Tallo handled the renter's showing-day logistics — attendance, getting in, or reaching the showing contact.",
  },
  COLLECT_PROPERTY_FEEDBACK: {
    label: 'Tour feedback',
    body: 'Collects post-tour impressions — useful signal for you even when the renter does not move forward.',
  },
  /** Retired into HANDLE_APPLICATION — kept for the historical rows still carrying this key. */
  RENTER_WANTS_TO_APPLY_FOR_THE_PROPERTY: {
    label: 'Applying',
    body: 'The renter wants to apply — Tallo shepherds them toward the application and keeps you posted.',
  },
  /**
   * Retired into HANDLE_APPLICATION. Unlike the other two it was unroutable before the merge, so
   * historical rows are unlikely — it gets the merged copy rather than copy of its own.
   */
  RENTER_HAS_SEEN_PROPERTY: {
    label: 'Application',
    body: 'Handles the application side of the thread — explaining how applying works here, and moving a renter who wants to apply toward the invite that comes from you after the showing.',
  },
  /** The merged application strategy: the three retired keys above plus the post-tour intent to apply. */
  HANDLE_APPLICATION: {
    label: 'Application',
    body: 'Handles the application side of the thread — explaining how applying works here, and moving a renter who wants to apply toward the invite that comes from you after the showing.',
  },
  SPEAK_TO_HUMAN: {
    label: 'Human requested',
    body: 'The renter asked for a person — Tallo hands off instead of pushing on.',
  },
  HANDLE_IDENTITY_VERIFICATION: {
    label: 'ID verification',
    body: 'Tallo worked the verification with the renter and nudged them through the check. This property requires it before any tour — government ID + a matching selfie; self-guided door access stays held until they pass.',
  },
};

/** Compact verb-first fragments composed into chip labels ("Move-in + credit ✓"). */
export const stepShortLabels: Record<string, string> = {
  DISCUSS_MOVE_IN_DATE: 'move-in',
  DISCUSS_CREDIT_SCORE: 'credit',
  DISCUSS_INCOME: 'income',
  SCHEDULE_SHOWING: 'booking the tour',
  RESCHEDULE_SHOWING: 'rebooking the tour',
  ANSWER_PROPERTY_QUESTION: 'answering a question',
  /** Retired → HANDLE_APPLICATION. */
  ANSWER_APPLICATION_PROCESS_QUESTION: 'explaining the application',
  /** Retired → HANDLE_SHOWING_LOGISTICS. */
  RENTER_CONFIRMS_SHOWING: 'attendance confirmed ✓',
  /** Rows written before the step recorded its leaf; newer rows go through showingLogisticsCopy. */
  HANDLE_SHOWING_LOGISTICS: 'showing logistics',
  COLLECT_PROPERTY_FEEDBACK: 'tour feedback',
  /** Retired → HANDLE_APPLICATION. */
  RENTER_WANTS_TO_APPLY_FOR_THE_PROPERTY: 'moving to application',
  /** Retired → HANDLE_APPLICATION. */
  RENTER_HAS_SEEN_PROPERTY: 'handling the application',
  HANDLE_APPLICATION: 'handling the application',
  SPEAK_TO_HUMAN: 'human requested',
  HANDLE_IDENTITY_VERIFICATION: 'handling ID verification',
};

export interface FollowUpMeta {
  /** Chip label fragment; a function because the copy can depend on the payload (channel switch). */
  piece: (payload: FollowUpTracePayload) => string;
  /** A function where the payload changes what the follow-up was for (a missed self-guided tour). */
  body: string | ((payload: FollowUpTracePayload) => string);
}

export function followUpBody(meta: FollowUpMeta, payload: FollowUpTracePayload): string {
  return typeof meta.body === 'function' ? meta.body(payload) : meta.body;
}

/** Keyed by BE FollowUpTypeEnum wire values. */
export const followUpMeta: Record<string, FollowUpMeta> = {
  'Renter Stopped Responding': {
    piece: (payload) => (payload.channelSwitched ? 're-engaging by text' : 'follow-up'),
    body: 'No reply for a while, so Tallo nudged instead of letting the lead die — this is where most un-managed leads are lost.',
  },
  'Showing Reminder Follow Up': {
    piece: () => 'tour reminder',
    body: 'Automatic reminder ahead of the confirmed tour, asking the renter to confirm attendance — the no-show insurance.',
  },
  'Showing Cancellation Warning Follow Up': {
    piece: () => 'last call to confirm',
    body: "The renter hasn't confirmed attendance or finished the ID verification you require, so Tallo warns that the in-person or virtual showing will be released unless they do.",
  },
  'Post Showing Follow Up': {
    piece: (payload) => (payload.noAccess ? 'offered another time' : 'post-tour check-in'),
    body: (payload) =>
      payload.noAccess
        ? "The renter never got the door code for the self-guided tour, so they couldn't have gone in — Tallo asked whether they'd like to find another time."
        : 'Follows up after the tour to collect impressions and move interested renters toward applying.',
  },
  'Application Invite Follow Up': {
    piece: (payload) => (payload.channelSwitched ? 'application nudge by text' : 'application nudge'),
    body: 'The renter had not started the application you sent, so Tallo checked whether they are still interested and what is holding them back.',
  },
  'Post Showing Feedback Reminder': {
    piece: () => 'post-tour reminder',
    body: 'The renter never answered the check-in after their tour, so Tallo asked once more what they made of the place and whether they want to move ahead.',
  },
};

export interface StepCopy {
  /** Panel breakdown label. */
  label: string;
  /** Chip segment. */
  short: string;
  body: string;
}

const attendanceConfirmedBody =
  'The renter confirmed they will attend, so the reminder loop stops and the showing stays on the calendar.';

/** Leaves that put a way in into the renter's hands. */
const doorAccessLeaves = ['share_access', 'let_in_now', 'replace_code'];

/** What a door-access leaf did, in the owner's terms. */
const doorAccessBodies: Record<string, string> = {
  share_access:
    'The renter asked how to get in, so Tallo sent them the entry details on file for this self-guided tour.',
  let_in_now:
    'The renter turned up outside their window while the home was free, so Tallo let them in — a lockbox code that works now, or your written entry instructions.',
  replace_code: "The renter's lockbox code didn't work, so Tallo minted a fresh one and sent it.",
};

/** Everything else the step can do, in the owner's terms. */
const logisticsBodies: Record<string, string> = {
  access_pending:
    "The renter wanted the entry details but none are on file, so Tallo asked you for them and told the renter they're on the way.",
  access_failure_escalated:
    "The renter's code or entry instructions didn't work. Tallo alerted the showing agent (or you, with no agent) and gave the renter someone to call.",
  verification_required:
    "Door access stays held until the renter passes this property's ID check — Tallo sent them the verification link instead.",
  verification_failed:
    "The renter's ID check could not be completed, so the showing came off the schedule and no door access was shared.",
  access_on_showing_day:
    'The renter asked for the entry details before the tour day. Access only goes out on the day of the tour, so Tallo told them they will get it then.',
};

const logisticsFallbackBody =
  'Same-day showing logistics — lateness, a time change, or trouble reaching the agent. Tallo pointed the renter to the showing contact.';

/**
 * Leaves whose answer was the confirmation itself or the way in. Elsewhere a confirmation rides along
 * with something the owner should see — lateness, nobody at the door, the wrong day, a code that failed.
 */
const attendanceLeaves = ['confirm_attendance', 'share_access', 'let_in_now'];

/**
 * The chip for HANDLE_SHOWING_LOGISTICS, from what it did. Three chips: "attendance confirmed ✓" when
 * the renter confirmed and Tallo answered with the confirmation or the way in, "door access sent" when
 * Tallo put a way in into their hands without one, and "showing logistics" for the rest — including a
 * confirmation that came with a problem, so "✓" never sits on a turn the owner needs to look at.
 */
export function showingLogisticsCopy(payload: Pick<StrategyTracePayload, 'leaf' | 'renterConfirmed'>): StepCopy {
  const leaf = payload.leaf ?? '';

  if (payload.renterConfirmed && attendanceLeaves.includes(leaf)) {
    return {
      label: 'Attendance confirmed',
      short: 'attendance confirmed ✓',
      body: doorAccessLeaves.includes(leaf)
        ? "The renter confirmed they're coming, and Tallo handed them the entry details on file so they can let themselves in."
        : attendanceConfirmedBody,
    };
  }

  if (doorAccessLeaves.includes(leaf)) {
    return { label: 'Door access', short: 'door access sent', body: doorAccessBodies[leaf] };
  }

  return {
    label: 'Showing logistics',
    short: 'showing logistics',
    body: logisticsBodies[leaf] ?? logisticsFallbackBody,
  };
}
